import produce from "immer";
import { createAction, createReducer } from "redux-act";
import undoable from "redux-undo";

import { reducerRegistry } from "../reducerRegistry";
import {
  ClassificationParameter,
  ClassificationSegmentValues,
  ClassificationValues,
} from "../../interfaces/classification";
import { ParameterValueWithSymbol } from "../../interfaces/style";

export interface ClassificationState {
  field?: ClassificationValueUpdate["field"];
  parameter?: ClassificationParameter;
  values?: ClassificationValues;
  segments?: ClassificationSegmentValues;
  defaultValue?: ParameterValueWithSymbol;
  defaultTitle?: string;
  attributeName?: string;
  error?: string;
}

export const reducerName = "classification";

const initialState: ClassificationState = {};

export type ClassificationValueUpdate = {
  field: "parameterValue" | "from" | "to" | "title";
  index: number;
  value: ParameterValueWithSymbol;
  selectedSegment?: number;
};

export const undo = createAction("classification/undo");
export const redo = createAction("classification/redo");
export const resetClassificationHistory = createAction("classification/reset-history");
export const setClassificationValues = createAction<ClassificationValues>("classification/set-values");
export const setClassification = createAction<ClassificationState>("classification/set-classification");
export const createClassificationSegment = createAction<number>("classification/create-segment");
export const setClassificationSegment = createAction<ClassificationState>("classification/set-segment-classification");
export const resetClassification = createAction("classification/reset-classification");
export const setClassificationField = createAction<{
  field: ClassificationValueUpdate["field"];
  selectedSegment: number;
}>("classification/set-field");
export const setClassificationParameter = createAction<{ parameter: ClassificationParameter; selectedSegment: number }>(
  "classification/set-parameter",
);
export const setClassificationDefaultValue = createAction<{
  defaultValue: ParameterValueWithSymbol;
  selectedSegment: number;
  valueIndex?: number;
}>("classification/set-default-value");
export const setClassificationDefaultTitle = createAction<{ defaultTitle: string; selectedSegment: number }>(
  "classification/set-default-title",
);
export const updateClassificationValue = createAction<ClassificationValueUpdate>("classification/update-value");
export const updateClassificationValues =
  createAction<Omit<ClassificationValueUpdate, "index">>("classification/update-values");
export const deleteClassificationValue = createAction<{ index: number; selectedSegment?: number }>(
  "classification/delete-value",
);
export const setClassificationError = createAction<string>("classification/set-error");

export const baseReducer = createReducer({}, initialState);

export const reducer = undoable(baseReducer, {
  undoType: undo.getType(),
  redoType: redo.getType(),
  limit: 10,
  ignoreInitialState: true,
  syncFilter: true,
  filter(action, currentState, { present }) {
    const valuesChanges = currentState.values !== present.values;
    const defaultValueChanges = currentState.defaultValue !== present.defaultValue;

    return Boolean(present.values) && Boolean(present.defaultValue) && (valuesChanges || defaultValueChanges);
  },
  clearHistoryType: resetClassificationHistory.getType(),
});

baseReducer.on(setClassificationValues, (state, values) => ({ ...state, values }));
baseReducer.on(setClassification, (_, classification) => classification);
baseReducer.on(setClassificationError, (state, error) => ({ ...state, error }));
baseReducer.on(createClassificationSegment, (state, index) => ({
  ...state,
  segments: { ...state.segments, [index]: { ...state, segments: null } },
}));
baseReducer.on(setClassificationSegment, (state, segment) => ({
  ...state,
  segments: { ...state.segments, ...segment },
}));
baseReducer.on(resetClassification, () => initialState);
baseReducer.on(
  setClassificationField,
  produce((state, { field, selectedSegment }) => {
    if (selectedSegment !== undefined) {
      state.segments[selectedSegment].field = field;
    } else {
      state.field = field;
    }
  }),
);
baseReducer.on(
  setClassificationParameter,
  produce((state, { parameter, selectedSegment }) => {
    if (selectedSegment !== undefined) {
      state.segments[selectedSegment].parameter = parameter;
    } else {
      state.parameter = parameter;
    }
  }),
);
baseReducer.on(
  setClassificationDefaultValue,
  produce((state, { defaultValue, selectedSegment }) => {
    if (selectedSegment !== undefined) {
      state.segments[selectedSegment].defaultValue = defaultValue;
    } else {
      state.defaultValue = defaultValue;
    }
  }),
);
baseReducer.on(
  setClassificationDefaultTitle,
  produce((state, { defaultTitle, selectedSegment }) => {
    if (selectedSegment !== undefined) {
      state.segments[selectedSegment].defaultTitle = defaultTitle;
    } else {
      state.defaultTitle = defaultTitle;
    }
  }),
);

baseReducer.on(
  deleteClassificationValue,
  produce((state, { index, selectedSegment }) => {
    if (selectedSegment !== undefined) {
      state.segments[selectedSegment].values.splice(index, 1);
    } else {
      state.values.splice(index, 1);
    }
  }),
);

baseReducer.on(
  updateClassificationValue,
  produce((state, { field, value, index, selectedSegment }) => {
    const newValue = value;
    const nextValue =
      selectedSegment !== undefined ? state.segments[selectedSegment].values[index + 1] : state.values[index + 1];

    if (field === "to" && nextValue) {
      if (value === null && nextValue.to === null) {
        if (selectedSegment !== undefined) {
          state.segments[selectedSegment].values[index][field] = 0;
        } else {
          state.values[index][field] = 0;
        }
        nextValue.from = 0;
      } else {
        nextValue.from = value;
      }
    }
    if (selectedSegment !== undefined) {
      state.segments[selectedSegment].values[index][field] = newValue;
    } else {
      state.values[index][field] = newValue;
    }
  }),
);

baseReducer.on(
  updateClassificationValues,
  produce((state, { field, value: fieldValue, selectedSegment }) => {
    const values = selectedSegment !== undefined ? state.segments?.[selectedSegment]?.values : state.values;
    const newValues = (values || []).map(value => ({
      ...value,
      [field]: fieldValue,
    }));

    if (selectedSegment !== undefined) {
      state.segments[selectedSegment].values = newValues;
    } else {
      state.values = newValues;
    }
  }),
);

reducerRegistry.register(reducerName, reducer);
